export function focusErrorField(field: string, noticeSelector: string) {
    const ids: any = {
        app_key: 'hn-kis-app-key',
        app_secret: 'hn-kis-app-secret',
        account: 'hn-kis-account',
        product_code: 'hn-kis-product',
        hts_id: 'hn-kis-hts',
        kis_current_password: 'hn-kis-current-password',
        kis_disconnect_password: 'hn-kis-disconnect-password',
        budget_mode: 'hn-budget-auto',
        live_budget: 'hn-live-budget',
        daily_loss_limit_pct: 'hn-daily-loss',
        order_ttl_seconds: 'hn-order-ttl',
        live_approval: 'hn-live-approval',
        strategy_current_password: 'hn-strategy-current-password'
    };
    setTimeout(() => {
        const target = field ? document.getElementById(ids[field]) : null;
        const notice = document.querySelector(noticeSelector) as HTMLElement;
        const behavior = typeof window.matchMedia === 'function'
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth';
        (target || notice)?.focus({ preventScroll: true });
        (target || notice)?.scrollIntoView({ block: 'center', behavior });
    }, 20);
}

export function livePrerequisites(data: any, accountReady: boolean) {
    return [
        { key: 'target', label: '운용 종목', ready: !!data.target, link: '/target' },
        { key: 'kis', label: 'KIS 정보', ready: !!data.kis_credentials_saved, link: '/settings#kis' },
        {
            key: 'live_budget',
            label: '운용 금액',
            ready: !!data.strategy?.live_approved && (
                data.strategy?.budget_mode === 'AUTO'
                || Number(data.strategy?.live_budget || 0) >= 100000
            ),
            link: '/settings#strategy'
        },
        { key: 'account', label: '계좌 확인', ready: accountReady, link: '' }
    ];
}

export function buyAllocationPreview(data: any, dirty: boolean) {
    const checked = data.live?.preflight;
    const version = data.strategy_context?.next_cycle?.version;
    if (dirty || checked?.integrity !== true || checked?.strategy_version !== version) return '';
    const quantities = checked?.buy_allocation?.quantities;
    return Array.isArray(quantities) && quantities.length
        ? quantities.map((value: number) => `${value}주`).join(' → ') : '';
}

export function automaticBudget(data: any) {
    const preflight = data.live?.preflight;
    return preflight?.integrity === true && preflight?.budget_mode === 'AUTO'
        ? Number(preflight.resolved_budget || 0) : 0;
}

export function connectionStatus(data: any, checking: boolean, dirty: boolean) {
    if (checking) return 'checking';
    if (dirty && data.kis_credentials_saved) return 'dirty';
    return data.kis_connection?.status || 'not_configured';
}

export function connectionLabel(status: string) {
    const labels: any = {
        not_configured: '연결 정보 없음',
        unchecked: '연결 확인 전',
        checking: '확인 중',
        connected: 'KIS 연결됨',
        failed: '연결 실패',
        dirty: '저장 필요'
    };
    return labels[status] || '연결 확인 전';
}

export function executionModeLabel(value: any) {
    const labels: any = {
        LIVE: '실전',
        SHADOW: '분석',
        NEXT: '다음 사이클'
    };
    return labels[String(value || '').toUpperCase()] || '운용';
}

export function productLabel(productCode: any) {
    const code = String(productCode || '01');
    const labels: any = {
        '01': '01 · 종합계좌',
        '22': '22 · 개인연금',
        '29': '29 · 퇴직연금'
    };
    return labels[code] || code;
}

export function minuteStatus(data: any) {
    return data.minute_data?.status || 'not_started';
}

export function minuteLabel(data: any) {
    const minuteData = data.minute_data || {};
    if (minuteData.status === 'running') {
        return `${minuteData.progress?.completed_days || 0}/${minuteData.progress?.total_days || 0}일 수집 중`;
    }
    if (minuteData.status === 'completed') {
        return `1분봉 ${Number(minuteData.result?.stored_count || 0).toLocaleString('ko-KR')}개`;
    }
    if (minuteData.status === 'partial') {
        return `1분봉 ${Number(minuteData.result?.stored_count || 0).toLocaleString('ko-KR')}개 · 일부 누락`;
    }
    if (minuteData.status === 'failed') {
        const stored = Number(minuteData.result?.stored_count || 0);
        return stored ? `수집 중단 · 1분봉 ${stored.toLocaleString('ko-KR')}개 유지` : '수집 중단';
    }
    return data.target ? '수집 전' : '종목 선택 필요';
}

export function minuteDetail(data: any) {
    const target = data.target;
    if (!target) return '운용 종목을 선택하면 1분봉을 수집할 수 있습니다.';
    const minuteData = data.minute_data || {};
    if (['completed', 'partial'].includes(minuteData.status)) {
        const start = storedDate(minuteData.result?.stored_from)
            || shortDate(minuteData.range?.start);
        const end = storedDate(minuteData.result?.stored_to)
            || shortDate(minuteData.range?.end);
        const quality = [
            countLabel('누락', minuteData.result?.missing_count),
            countLabel('검증 제외', minuteData.result?.invalid_count)
        ].filter(Boolean);
        return [
            `${target.name} · ${target.code}`,
            `${start}–${end}`,
            `${Number(minuteData.result?.trading_days || 0)}거래일`,
            ...quality
        ].join(' · ');
    }
    if (minuteData.status === 'running') {
        const current = shortDate(minuteData.progress?.current_date);
        return `${target.name} · ${target.code}${current ? ` · ${current} 처리 중` : ''}`;
    }
    return `${target.name} · ${target.code} · ${minuteData.message || ''}`;
}

export function minuteLatest(data: any) {
    const finishedAt = data.minute_data?.finished_at;
    const latestBar = data.minute_data?.result?.stored_to;
    return [
        finishedAt ? `완료 ${formatCheckedAt(finishedAt)}` : '',
        latestBar ? `최근 봉 ${formatCheckedAt(latestBar)}` : ''
    ].filter(Boolean).join(' · ');
}

export function formatMoney(value: any) {
    const amount = Math.max(0, Math.floor(Number(value) || 0));
    return `${amount.toLocaleString('ko-KR')}원`;
}

export function formatCheckedAt(value: any) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('ko-KR', {
        timeZone: 'Asia/Seoul',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
}

export function versionLabel(value: any) {
    const match = String(value || '').match(/strategy-(v[0-9.]+)/i);
    return match ? match[1] : String(value || '버전 없음');
}

export function percentPlan(values: any, prefix: string = '') {
    if (!Array.isArray(values) || values.length === 0) return '-';
    return values.map((value: any) => `${prefix}${Number(value)}%`).join(' / ');
}

function countLabel(label: string, value: any) {
    const count = Number(value || 0);
    return count ? `${label} ${count.toLocaleString('ko-KR')}개` : '';
}

function shortDate(value: any) {
    if (!value) return '';
    const parts = String(value).slice(0, 10).split('-');
    return parts.length === 3 ? `${parts[1]}.${parts[2]}` : String(value);
}

function storedDate(value: any) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const parts = new Intl.DateTimeFormat('ko-KR', {
        timeZone: 'Asia/Seoul',
        month: '2-digit',
        day: '2-digit'
    }).formatToParts(date);
    const month = parts.find((part: any) => part.type === 'month')?.value;
    const day = parts.find((part: any) => part.type === 'day')?.value;
    return month && day ? `${month}.${day}` : '';
}
